import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, providerImage } from "@/lib/api";
import { cityCoords } from "@/lib/cityGeo";
import ProviderCard, { startingPrice } from "@/components/ProviderCard";
import ProviderProfileModal from "@/components/ProviderProfileModal";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useCompare } from "@/context/CompareContext";
import { Map as MapIcon, List, RotateCcw, SlidersHorizontal, Loader2, MapPin } from "lucide-react";

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const MC_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css";
const MC_CSS2 = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
const MC_JS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";
const INDIA_CENTER = [20.5937, 78.9629];

function loadCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement("link");
  l.rel = "stylesheet"; l.href = href;
  document.head.appendChild(l);
}
const _scriptPromises = {};
function loadScript(src) {
  if (!_scriptPromises[src]) {
    _scriptPromises[src] = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded) resolve();
        else { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); }
        return;
      }
      const s = document.createElement("script");
      s.src = src; s.async = true;
      s.onload = () => { s.dataset.loaded = "1"; resolve(); };
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return _scriptPromises[src];
}

const clusterColor = (n) => (n >= 16 ? "#7209B7" : n >= 6 ? "#1D4ED8" : "#2A9D8F");

export default function MapView() {
  const navigate = useNavigate();
  const compare = useCompare();
  const [providers, setProviders] = useState(null);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState("map");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [specialty, setSpecialty] = useState("all");
  const [accs, setAccs] = useState([]); // JCI/NABH/NABL
  const [minRating, setMinRating] = useState(3);

  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const LRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { document.title = "Hospital Map | SarvaSeva"; }, []);

  // Load data
  useEffect(() => {
    api.get("/v1/providers").then((r) => setProviders(Array.isArray(r.data) ? r.data : [])).catch(() => setProviders([]));
  }, []);

  // Load leaflet libs
  useEffect(() => {
    let cancelled = false;
    loadCss(LEAFLET_CSS); loadCss(MC_CSS); loadCss(MC_CSS2);
    (async () => {
      try {
        await loadScript(LEAFLET_JS);
        await loadScript(MC_JS);
        if (!cancelled) { LRef.current = window.L; setReady(true); }
      } catch { /* leaflet cdn failed */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const specialties = useMemo(
    () => Array.from(new Set((providers || []).flatMap((p) => p.specialties || []))).sort(),
    [providers]);

  const filtered = useMemo(() => (providers || []).filter((p) => {
    if (specialty !== "all" && !(p.specialties || []).includes(specialty)) return false;
    if (accs.length && !accs.some((a) => (p.accreditation || []).includes(a))) return false;
    if ((p.rating || 0) < minRating) return false;
    return true;
  }), [providers, specialty, accs, minRating]);

  // Init map once
  useEffect(() => {
    if (!ready || mode !== "map" || !providers || !containerRef.current || mapRef.current) return;
    const L = LRef.current;
    const map = L.map(containerRef.current, { scrollWheelZoom: true }).setView(INDIA_CENTER, 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    clusterRef.current = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const n = cluster.getChildCount();
        const c = clusterColor(n);
        return L.divIcon({
          html: `<div style="background:${c};color:#fff;width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff">${n}</div>`,
          className: "", iconSize: [38, 38],
        });
      },
    });
    map.addLayer(clusterRef.current);
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, [ready, mode, providers]);

  // Rebuild markers on filter change
  useEffect(() => {
    if (!ready || mode !== "map" || !clusterRef.current) return;
    const L = LRef.current;
    const cluster = clusterRef.current;
    cluster.clearLayers();
    filtered.forEach((p) => {
      const [lat, lng] = cityCoords(p.city);
      // slight jitter so co-located hospitals don't stack exactly
      const jlat = lat + (Math.random() - 0.5) * 0.04;
      const jlng = lng + (Math.random() - 0.5) * 0.04;
      const cat = p.category || "Surgical";
      const pinColor = { "Surgical": "#0D5C75", "Clinical & Diagnostics": "#1D4ED8",
        "Rehabilitation & Recovery": "#10B981", "Wellness & Alternative": "#D97706",
        "Specialized Care": "#8B5CF6", "Women & Children": "#EC4899" }[cat] || "#0D5C75";
      const icon = L.divIcon({
        html: `<div style="width:22px;height:22px;background:${pinColor};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
        className: "", iconSize: [22, 22], iconAnchor: [11, 22],
      });
      const marker = L.marker([jlat, jlng], { icon });
      marker.bindPopup(buildPopup(p, { navigate, setSelected, setOpen, compare }), { minWidth: 220 });
      cluster.addLayer(marker);
    });
  }, [filtered, ready, mode]); // eslint-disable-line

  const resetView = () => mapRef.current && mapRef.current.setView(INDIA_CENTER, 5);

  const FilterPanel = (
    <div className="space-y-6" data-testid="map-filter-panel">
      <div>
        <div className="text-sm font-semibold text-[#101828] mb-2">Specialty</div>
        <Select value={specialty} onValueChange={setSpecialty}>
          <SelectTrigger className="w-full h-9" data-testid="map-filter-specialty"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[300px]"><SelectItem value="all">All Specialties</SelectItem>
            {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-sm font-semibold text-[#101828] mb-2">Accreditation</div>
        <div className="space-y-2">
          {["JCI", "NABH", "NABL"].map((a) => (
            <label key={a} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`map-acc-${a}`}>
              <input type="checkbox" checked={accs.includes(a)}
                onChange={(e) => setAccs((prev) => e.target.checked ? [...prev, a] : prev.filter((x) => x !== a))}
                className="h-4 w-4 rounded accent-[#0D5C75]" />
              {a}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold text-[#101828] mb-2">Minimum rating: <span className="text-[#0D5C75]">{minRating.toFixed(1)}</span></div>
        <input type="range" min="3" max="5" step="0.1" value={minRating}
          onChange={(e) => setMinRating(Number(e.target.value))}
          className="w-full accent-[#0D5C75]" data-testid="map-rating-slider" />
      </div>
      <div className="rounded-xl bg-[#E9F5F9] px-3 py-2.5 text-sm font-semibold text-[#0D5C75]" data-testid="map-results-count">
        Showing {filtered.length} of {providers?.length || 0} hospitals
      </div>
    </div>
  );

  return (
    <div className="bg-[#F7F9FC] min-h-screen" data-testid="map-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-[#101828] flex items-center gap-2"><MapIcon size={26} className="text-[#0D5C75]" /> Hospital Map</h1>
            <p className="text-sm text-muted-foreground mt-1">Explore {providers?.length || 118} partner hospitals across India.</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile filter trigger */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="map-mobile-filter-btn"><SlidersHorizontal size={16} className="mr-1.5" /> Filters</Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto">
                  <div className="pt-6">{FilterPanel}</div>
                </SheetContent>
              </Sheet>
            </div>
            {mode === "map" && <Button variant="outline" className="rounded-full" onClick={resetView} data-testid="map-reset-btn"><RotateCcw size={15} className="mr-1.5" /> Reset View</Button>}
            <div className="flex rounded-full border border-[#E4E7EC] bg-white overflow-hidden">
              <button onClick={() => setMode("map")} data-testid="map-view-toggle"
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${mode === "map" ? "bg-[#0D5C75] text-white" : "text-[#475467]"}`}><MapIcon size={15} /> Map</button>
              <button onClick={() => setMode("list")} data-testid="list-view-toggle"
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${mode === "list" ? "bg-[#0D5C75] text-white" : "text-[#475467]"}`}><List size={15} /> List</button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-20 rounded-2xl bg-white border border-[#E4E7EC] p-5">{FilterPanel}</div>
          </aside>

          <div className="flex-1 min-w-0">
            {!providers ? (
              <div className="py-24 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>
            ) : mode === "map" ? (
              <div className="relative">
                {!ready && <div className="absolute inset-0 z-10 grid place-items-center bg-white/70 rounded-2xl"><Loader2 className="animate-spin text-[#0D5C75]" /></div>}
                <div ref={containerRef} data-testid="leaflet-map"
                  className="h-[70vh] min-h-[480px] w-full rounded-2xl overflow-hidden border border-[#E4E7EC] z-0" />
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-4">Showing {filtered.length} hospital{filtered.length === 1 ? "" : "s"}</p>
                {filtered.length === 0 ? (
                  <div className="py-16 text-center text-muted-foreground" data-testid="map-list-empty"><MapPin size={36} className="mx-auto mb-3" />No hospitals match your filters.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch" data-testid="map-list-grid">
                    {filtered.map((p, i) => <ProviderCard key={p.id} p={p} i={i} onView={(x) => { setSelected(x); setOpen(true); }} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ProviderProfileModal provider={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

// Builds a DOM node popup with wired action buttons (avoids raw-HTML handler issues).
function buildPopup(p, { navigate, setSelected, setOpen, compare }) {
  const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
  const el = document.createElement("div");
  el.style.minWidth = "200px";
  const accs = (p.accreditation || []).map((a) =>
    `<span style="background:${a === "JCI" ? "#D4A017" : "#1D4ED8"};color:#fff;font-size:10px;padding:1px 6px;border-radius:6px;margin-right:4px">${a}</span>`).join("");
  const specs = (p.specialties || []).slice(0, 2).join(", ");
  el.innerHTML = `
    <div style="font-weight:800;font-size:14px;color:#101828">${p.name}</div>
    <div style="font-size:12px;color:#667085;margin:2px 0 6px">★ ${p.rating ?? "—"} · ${p.city}</div>
    <div style="margin-bottom:6px">${accs}</div>
    <div style="font-size:12px;color:#475467">${specs}</div>
    <div style="font-size:13px;color:#0D5C75;font-weight:700;margin-top:4px">From ${money(startingPrice(p))}</div>
    <div style="display:flex;gap:6px;margin-top:8px">
      <button data-act="view" style="flex:1;background:#0D5C75;color:#fff;border:none;border-radius:999px;padding:6px 8px;font-size:12px;font-weight:600;cursor:pointer">View Profile</button>
      <button data-act="compare" style="background:#E9F5F9;color:#0D5C75;border:none;border-radius:999px;padding:6px 8px;font-size:12px;font-weight:600;cursor:pointer">+ Compare</button>
    </div>`;
  el.querySelector('[data-act="view"]').addEventListener("click", () => { setSelected(p); setOpen(true); });
  el.querySelector('[data-act="compare"]').addEventListener("click", () => compare && compare.toggle(p.id));
  return el;
}
